/**
 * Async LLM classification refinement.
 * Runs AFTER events are stored with keyword classification.
 * Never blocks UI — debounced with circuit breaker and exponential backoff.
 */

import type { NormalizedEvent } from '@/sources/base-source';
import type { ClassifierResult } from './types';
import { executeWithFallback } from './fallback-chain';
import { CLASSIFY_SYSTEM_PROMPT, buildClassifyPrompt } from './prompts';
import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';

/** Max events to refine per batch cycle. */
const BATCH_BUDGET = 5;

/** Keyword confidence threshold — below this, LLM refinement triggers. */
const KEYWORD_CONFIDENCE_THRESHOLD = 0.6;

/** Track which events have been refined to avoid duplicate work. */
const refinedIds = new Set<string>();
/** Absolute cap for refinedIds. Pruned to half when exceeded. */
const MAX_REFINED_IDS = 100_000;

// --- Circuit breaker ---
/** Consecutive batches where every refinement failed. */
let consecutiveAllFailBatches = 0;
/** After this many consecutive all-fail batches, stop scheduling. */
const CIRCUIT_BREAKER_THRESHOLD = 3;
/** Base debounce interval (ms). */
const BASE_DEBOUNCE_MS = 5_000;
/** Max backoff interval (ms) — 5 minutes. */
const MAX_DEBOUNCE_MS = 300_000;

/**
 * Parse LLM classification response JSON.
 * Returns null on any parse failure — never throws.
 */
function parseClassificationResponse(text: string): (ClassifierResult & { severity: string; tags: string[] }) | null {
  try {
    // Extract JSON from response (LLM may wrap in markdown code blocks)
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (!jsonMatch) return null;

    const parsed = JSON.parse(jsonMatch[0]) as {
      severity?: string;
      category?: string;
      tags?: string[];
      confidence?: number;
    };

    const validSeverities = ['critical', 'high', 'medium', 'low', 'info'];
    const severity = (parsed.severity && validSeverities.includes(parsed.severity))
      ? parsed.severity
      : 'info';

    const confidence = typeof parsed.confidence === 'number'
      ? Math.max(0, Math.min(1, parsed.confidence))
      : 0.7;

    return {
      category: parsed.category ?? 'unknown',
      confidence,
      method: 'llm' as const,
      severity,
      tags: Array.isArray(parsed.tags) ? parsed.tags.filter(t => typeof t === 'string').slice(0, 4) : [],
    };
  } catch {
    return null;
  }
}

/**
 * Refine classification for a single event using LLM.
 * Updates the event in the store if LLM confidence exceeds keyword confidence.
 * Returns true if refinement succeeded, false on any failure.
 */
async function refineEvent(event: NormalizedEvent): Promise<boolean> {
  if (refinedIds.has(event.id)) return false;
  refinedIds.add(event.id);

  try {
    const response = await executeWithFallback({
      prompt: buildClassifyPrompt(event.title, event.description),
      systemPrompt: CLASSIFY_SYSTEM_PROMPT,
      maxTokens: 150,
      temperature: 0.1,
    });

    const result = parseClassificationResponse(response.text);
    if (!result) return false;

    if (result.confidence > (event.confidence ?? 0)) {
      const events = [...appStore.getState().events];
      const idx = events.findIndex(e => e.id === event.id);
      if (idx !== -1) {
        const existing = events[idx];
        events[idx] = {
          ...existing,
          severity: result.severity as NormalizedEvent['severity'],
          confidence: result.confidence,
          tags: [...new Set([...(existing.tags ?? []), ...result.tags])],
        };
        appStore.setState({ events });
      }

      eventBus.emit('ai:classify:complete', { eventId: event.id, result });
    }

    return true;
  } catch {
    // Silently fail — keyword classification still stands
    return false;
  }
}

/** Debounce timer for refinement scheduling. */
let refinementTimer: ReturnType<typeof setTimeout> | null = null;

/** Current debounce interval with exponential backoff. */
function getDebounceMs(): number {
  if (consecutiveAllFailBatches === 0) return BASE_DEBOUNCE_MS;
  return Math.min(BASE_DEBOUNCE_MS * Math.pow(2, consecutiveAllFailBatches), MAX_DEBOUNCE_MS);
}

/**
 * Schedule async LLM refinement for recently added events.
 * Debounced with exponential backoff. Circuit breaker stops scheduling
 * after CIRCUIT_BREAKER_THRESHOLD consecutive all-fail batches.
 * Called from pipeline after event:added.
 */
export function scheduleRefinement(): void {
  // Circuit breaker: stop scheduling if AI providers are consistently failing
  if (consecutiveAllFailBatches >= CIRCUIT_BREAKER_THRESHOLD) return;
  if (refinementTimer !== null) return; // Already scheduled
  refinementTimer = setTimeout(() => {
    refinementTimer = null;
    void runRefinementBatch();
  }, getDebounceMs());
}

async function runRefinementBatch(): Promise<void> {
  const events = appStore.getState().events;

  // Prune stale IDs that are no longer in the event store, or when absolute cap exceeded
  if (refinedIds.size > events.length * 1.5 || refinedIds.size > MAX_REFINED_IDS) {
    const currentIds = new Set(events.map(e => e.id));
    for (const id of refinedIds) {
      if (!currentIds.has(id)) refinedIds.delete(id);
    }
  }

  // Find events that need refinement: no confidence set or below threshold, not yet refined
  const candidates = events
    .filter(e =>
      !refinedIds.has(e.id) &&
      (e.confidence === undefined || e.confidence < KEYWORD_CONFIDENCE_THRESHOLD),
    )
    .slice(-BATCH_BUDGET); // Most recent first (end of array)

  if (candidates.length === 0) return;

  let anySuccess = false;
  let consecutiveEventFailures = 0;
  for (const event of candidates) {
    const ok = await refineEvent(event);
    if (ok) {
      anySuccess = true;
      consecutiveEventFailures = 0;
    } else {
      consecutiveEventFailures++;
      // If first 2 events in this batch all fail, bail out early
      if (consecutiveEventFailures >= 2 && !anySuccess) break;
    }
  }

  // Track circuit breaker state
  if (anySuccess) {
    consecutiveAllFailBatches = 0;
  } else {
    consecutiveAllFailBatches++;
    if (consecutiveAllFailBatches >= CIRCUIT_BREAKER_THRESHOLD) {
      eventBus.emit('pipeline:error', {
        stage: 'ai-classifier',
        error: `LLM refinement circuit breaker tripped after ${CIRCUIT_BREAKER_THRESHOLD} consecutive failed batches`,
      });
    }
  }
}

/** Clear refinement tracking and reset circuit breaker (for testing or provider change). */
export function clearRefinementCache(): void {
  refinedIds.clear();
  consecutiveAllFailBatches = 0;
  if (refinementTimer !== null) {
    clearTimeout(refinementTimer);
    refinementTimer = null;
  }
}
